/**
 * Centralized formatting utilities.
 * All date/number/string transformations live here.
 */

// ── Date Formatters ─────────────────────────────────────────────────────────

const dateFormatter     = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium' })
const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
const timeFormatter     = new Intl.DateTimeFormat('en-IN', { timeStyle: 'short' })

export const formatDate     = (date) => dateFormatter.format(new Date(date))
export const formatDateTime = (date) => dateTimeFormatter.format(new Date(date))
export const formatTime     = (date) => timeFormatter.format(new Date(date))

/** Returns relative time string: "2 hours ago", "in 3 days" */
export function formatRelativeTime(date) {
  const rtf   = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  const delta = (new Date(date) - Date.now()) / 1000

  const thresholds = [
    { unit: 'year',   seconds: 31_536_000 },
    { unit: 'month',  seconds: 2_592_000  },
    { unit: 'week',   seconds: 604_800    },
    { unit: 'day',    seconds: 86_400     },
    { unit: 'hour',   seconds: 3_600      },
    { unit: 'minute', seconds: 60         },
    { unit: 'second', seconds: 1          },
  ]

  for (const { unit, seconds } of thresholds) {
    if (Math.abs(delta) >= seconds) {
      return rtf.format(Math.round(delta / seconds), unit)
    }
  }

  return 'just now'
}

// ── Number Formatters ───────────────────────────────────────────────────────

export const formatNumber   = (n) => new Intl.NumberFormat('en-IN').format(n)
export const formatCurrency = (n, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(n)
export const formatPercent  = (n, decimals = 1) => `${(n * 100).toFixed(decimals)}%`

// ── String Formatters ───────────────────────────────────────────────────────

export const capitalize    = (s = '') => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase()
export const titleCase     = (s = '') => s.split(' ').map(capitalize).join(' ')
export const truncate      = (s = '', len = 80) => s.length > len ? `${s.slice(0, len)}…` : s
export const slugify       = (s = '') =>
  s.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')

/** Combine venue name/address once, deduping repeated comma-separated segments. */
export function formatEventLocation(venueName, venueAddress) {
  const seen = new Set()
  const parts = []

  for (const raw of [venueName, venueAddress]) {
    for (const segment of String(raw ?? '').split(',')) {
      const value = segment.trim()
      if (!value) continue
      const key = value.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      parts.push(value)
    }
  }

  return parts.join(', ')
}

export function formatConnectionLabel(count = 0) {
  const n = Number(count) || 0
  return `${n} connection${n === 1 ? '' : 's'}`
}
